import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, ArrowLeft, Loader2, Copy, MessageCircle, Trash2, Link2, RefreshCw, Ban, UserSwitch, FileText, Download, CheckCircle2, Clock, XCircle } from "lucide-react";
import {
    fetchSalesTours, fetchSalesTour, createSalesTour, updateSalesTour, setSalesSchedule,
    shareSalesTour, cancelSalesTour, revokeSalesLink, regenerateSalesLink, reassignSalesTour,
    addSalesAdjustment, exportSalesToursCsv, fetchStaff, inr, formatApiError,
} from "@/lib/api";

const inp = "w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-saffron";
const lab = "block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1";

const STATUS_CHIP = {
    draft: "bg-stone-200 text-stone-600",
    shared: "bg-blue-100 text-blue-700",
    partially_paid: "bg-gold/20 text-yellow-700",
    fully_paid: "bg-green-100 text-green-700",
    cancelled: "bg-red-100 text-red-600",
};
const INST_CHIP = { pending: "bg-stone-200 text-stone-600", paid: "bg-green-100 text-green-700", failed: "bg-red-100 text-red-600", cancelled: "bg-red-100 text-red-600" };
const statusLabel = (s) => (s || "").replace(/_/g, " ");

const BLANK = {
    tour: { name: "", destination: "", start_date: "", end_date: "", days: "", nights: "", travellers: 2, itinerary: "", inclusions: [], exclusions: [], notes: "" },
    customer: { name: "", phone: "", email: "" },
    total_cost: "",
};

// All field helpers live at module scope — defining them inside a component
// remounts inputs on every keystroke and drops focus (fixed once in PackageManager).
const Txt = ({ label, value, onChange, ph, testid, type = "text", min }) => (
    <div><label className={lab}>{label}</label><input type={type} min={min} className={inp} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={ph} data-testid={testid} /></div>
);

function StatCard({ label, value, testid }) {
    return (
        <div className="rounded-xl border border-stone-200 bg-cream px-4 py-3" data-testid={testid}>
            <p className="text-[10px] font-bold uppercase tracking-wider text-stone-500">{label}</p>
            <p className="font-display font-black text-lg text-navy mt-0.5">{value}</p>
        </div>
    );
}

function ProposalForm({ initial, onSaved, onCancel, token }) {
    const [f, setF] = useState(initial);
    const [busy, setBusy] = useState(false);
    const isEdit = !!initial.id;
    const setT = (k, v) => setF((s) => ({ ...s, tour: { ...s.tour, [k]: v } }));
    const setC = (k, v) => setF((s) => ({ ...s, customer: { ...s.customer, [k]: v } }));

    const save = async () => {
        setBusy(true);
        try {
            const payload = {
                tour: { ...f.tour, travellers: +f.tour.travellers || 0, days: f.tour.days ? +f.tour.days : null, nights: f.tour.nights ? +f.tour.nights : null,
                        start_date: f.tour.start_date || null, end_date: f.tour.end_date || null,
                        inclusions: Array.isArray(f.tour.inclusions) ? f.tour.inclusions : [], exclusions: Array.isArray(f.tour.exclusions) ? f.tour.exclusions : [] },
                customer: { ...f.customer, email: f.customer.email || null },
                total_cost: +f.total_cost || 0,
            };
            const saved = isEdit ? await updateSalesTour(token, f.id, payload) : await createSalesTour(token, payload);
            toast.success(isEdit ? "Proposal updated" : `Proposal ${saved.ref} created`);
            onSaved(saved);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-8" data-testid="st-form">
            <div className="flex items-center justify-between">
                <button onClick={onCancel} data-testid="st-form-back" className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-navy hover:text-saffron"><ArrowLeft className="h-4 w-4" /> Back</button>
                <button onClick={save} disabled={busy} data-testid="st-form-save" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-saffron text-white text-xs font-display font-black uppercase tracking-wider hover:bg-saffron-dark disabled:opacity-60">
                    {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {isEdit ? "Save Changes" : "Create Proposal"}
                </button>
            </div>
            <div>
                <h3 className="font-display font-black text-navy border-b border-stone-200 pb-2">Tour Details</h3>
                <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="col-span-2"><Txt label="Tour / package name *" value={f.tour.name} onChange={(v) => setT("name", v)} ph="Corporate Offsite — Manali" testid="st-tour-name" /></div>
                    <Txt label="Destination *" value={f.tour.destination} onChange={(v) => setT("destination", v)} ph="Manali" testid="st-destination" />
                    <Txt label="Travellers *" type="number" min="1" value={f.tour.travellers} onChange={(v) => setT("travellers", v)} testid="st-travellers" />
                    <Txt label="Start date" type="date" value={f.tour.start_date} onChange={(v) => setT("start_date", v)} testid="st-start-date" />
                    <Txt label="End date" type="date" value={f.tour.end_date} onChange={(v) => setT("end_date", v)} testid="st-end-date" />
                    <Txt label="Days" type="number" min="0" value={f.tour.days} onChange={(v) => setT("days", v)} testid="st-days" />
                    <Txt label="Nights" type="number" min="0" value={f.tour.nights} onChange={(v) => setT("nights", v)} testid="st-nights" />
                    <div className="col-span-2 lg:col-span-4"><label className={lab}>Itinerary / tour description</label><textarea rows={4} className={inp} value={f.tour.itinerary} onChange={(e) => setT("itinerary", e.target.value)} placeholder="Day-wise plan, hotels, transfers…" data-testid="st-itinerary" /></div>
                    <div className="col-span-2"><label className={lab}>Inclusions (one per line)</label><textarea rows={3} className={inp} value={(f.tour.inclusions || []).join("\n")} onChange={(e) => setT("inclusions", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} data-testid="st-inclusions" /></div>
                    <div className="col-span-2"><label className={lab}>Exclusions (one per line)</label><textarea rows={3} className={inp} value={(f.tour.exclusions || []).join("\n")} onChange={(e) => setT("exclusions", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} data-testid="st-exclusions" /></div>
                    <div className="col-span-2 lg:col-span-4"><label className={lab}>Special instructions</label><textarea rows={2} className={inp} value={f.tour.notes} onChange={(e) => setT("notes", e.target.value)} data-testid="st-notes" /></div>
                </div>
            </div>
            <div>
                <h3 className="font-display font-black text-navy border-b border-stone-200 pb-2">Customer / Contact</h3>
                <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <Txt label="Full name *" value={f.customer.name} onChange={(v) => setC("name", v)} ph="Rahul Sharma" testid="st-cust-name" />
                    <Txt label="Mobile *" value={f.customer.phone} onChange={(v) => setC("phone", v)} ph="10-digit mobile" testid="st-cust-phone" />
                    <Txt label="Email" value={f.customer.email} onChange={(v) => setC("email", v)} ph="optional" testid="st-cust-email" />
                    <Txt label="Total tour cost (INR) *" type="number" min="1" value={f.total_cost} onChange={(v) => setF((s) => ({ ...s, total_cost: v }))} testid="st-total-cost" />
                </div>
            </div>
            <p className="text-[11px] text-stone-400">Your name and the timestamp are recorded automatically. The total locks once a payment schedule is set.</p>
        </div>
    );
}

function ScheduleBuilder({ prop, token, onChanged }) {
    const [count, setCount] = useState(prop.installments?.length || 2);
    const [rows, setRows] = useState(() => (prop.installments?.length ? prop.installments.map((x) => ({ pct: x.pct ?? "", amount: x.amount, due_date: x.due_date })) : [{ pct: 50, amount: "", due_date: "" }, { pct: 50, amount: "", due_date: "" }]));
    const [busy, setBusy] = useState(false);
    const locked = prop.paid_count > 0 || ["fully_paid", "cancelled"].includes(prop.status);
    const total = prop.total_cost;

    const setRow = (i, k, v) => setRows((rs) => rs.map((r, x) => (x === i ? { ...r, [k]: v } : r)));
    const changeCount = (n) => { setCount(n); setRows((rs) => Array.from({ length: n }, (_, i) => rs[i] || { pct: "", amount: "", due_date: "" })); };
    const computed = rows.map((r) => (r.pct !== "" && r.pct != null ? Math.round((total * +r.pct) / 100) : +r.amount || 0));
    if (computed.length) computed[computed.length - 1] += total - computed.reduce((a, b) => a + b, 0);
    const reconciles = computed.reduce((a, b) => a + b, 0) === total && computed.every((a) => a >= 1);

    const save = async () => {
        setBusy(true);
        try {
            const payload = rows.map((r) => ({ pct: r.pct === "" || r.pct == null ? null : +r.pct, amount: r.amount === "" || r.amount == null ? null : +r.amount, due_date: r.due_date }));
            const updated = await setSalesSchedule(token, prop.id, payload);
            toast.success("Payment schedule saved — amounts reconcile to the total");
            onChanged(updated);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not save schedule");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="st-schedule">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-display font-black text-navy">Instalment Schedule {locked && <span className="text-[10px] text-stone-400 font-bold uppercase tracking-wider">(locked — payment received)</span>}</h3>
                {!locked && (
                    <div className="flex gap-1.5" data-testid="st-sched-count">
                        {[2, 3].map((n) => (
                            <button key={n} onClick={() => changeCount(n)} className={`px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-wider ${count === n ? "bg-navy text-white" : "bg-stone-100 text-stone-500"}`}>{n} instalments</button>
                        ))}
                    </div>
                )}
            </div>
            <div className="mt-3 space-y-2">
                {rows.slice(0, count).map((r, i) => (
                    <div key={i} className="grid grid-cols-[70px_1fr_1fr_1fr_110px] gap-2 items-center" data-testid={`st-sched-row-${i}`}>
                        <span className="h-7 w-7 rounded-full bg-navy text-white text-xs font-black flex items-center justify-center">{i + 1}</span>
                        <input type="number" min="0" max="99" className={inp} value={r.pct ?? ""} disabled={locked} placeholder="% of total" onChange={(e) => setRow(i, "pct", e.target.value === "" ? "" : e.target.value)} data-testid={`st-sched-${i}-pct`} />
                        <input type="number" min="1" className={inp} value={r.amount ?? ""} disabled={locked || (r.pct !== "" && r.pct != null)} placeholder="₹ amount" onChange={(e) => setRow(i, "amount", e.target.value)} data-testid={`st-sched-${i}-amount`} />
                        <input type="date" className={inp} value={r.due_date} disabled={locked} onChange={(e) => setRow(i, "due_date", e.target.value)} data-testid={`st-sched-${i}-due`} />
                        <span className="text-xs font-bold text-navy" data-testid={`st-sched-${i}-computed`}>{computed[i] >= 1 ? inr(computed[i]) : "—"}</span>
                    </div>
                ))}
            </div>
            {!locked && (
                <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                    <p className={`text-xs font-bold ${reconciles ? "text-green-700" : "text-red-600"}`} data-testid="st-sched-reconcile">
                        {reconciles ? `Adds up to ${inr(total)} exactly — last instalment auto-adjusts rounding` : `Schedule must add up to ${inr(total)}`}
                    </p>
                    <button onClick={save} disabled={busy || !reconciles || rows.some((r) => !r.due_date)} data-testid="st-sched-save" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-navy text-white text-[11px] font-black uppercase tracking-wider hover:bg-navy-light disabled:opacity-50">
                        {busy && <Loader2 className="h-3 w-3 animate-spin" />} Save Schedule
                    </button>
                </div>
            )}
        </div>
    );
}

function ShareLinks({ prop, token, onChanged }) {
    const [busy, setBusy] = useState(false);
    const copy = (text) => { navigator.clipboard.writeText(text).then(() => toast.success("Link copied — paste it in WhatsApp or email")); };
    const share = async () => {
        setBusy(true);
        try {
            await shareSalesTour(token, prop.id);
            toast.success("Payment links are live");
            onChanged(await fetchSalesTour(token, prop.id)); // full reload: fresh links + audit entry render immediately
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not generate links"); }
        finally { setBusy(false); }
    };
    const act = async (fn, ...args) => {
        try { await fn(token, prop.id, ...args); onChanged(await fetchSalesTour(token, prop.id)); }
        catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Action failed"); }
    };
    const links = prop.share_links || {};

    return (
        <div data-testid="st-share">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-display font-black text-navy">Customer Payment Links</h3>
                <button onClick={share} disabled={busy || !prop.installments?.length || prop.status === "cancelled"} data-testid="st-share-btn" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-saffron text-white text-[11px] font-black uppercase tracking-wider hover:bg-saffron-dark disabled:opacity-50">
                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />} {prop.status === "draft" ? "Generate & Publish Links" : "Refresh Links"}
                </button>
            </div>
            {!prop.installments?.length && <p className="mt-2 text-xs text-stone-400">Save the instalment schedule first.</p>}
            <div className="mt-3 space-y-2">
                {(prop.installments || []).map((x) => (
                    <div key={x.n} className="rounded-xl border border-stone-200 p-3" data-testid={`st-link-row-${x.n}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-black text-navy">Instalment {x.n} — {inr(x.amount)}</span>
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${INST_CHIP[x.status] || INST_CHIP.pending}`} data-testid={`st-inst-${x.n}-status`}>{x.expired && x.status !== "paid" ? "expired" : x.status}</span>
                            {links[String(x.n)] && (
                                <span className="ml-auto flex gap-1.5">
                                    <button onClick={() => copy(links[String(x.n)])} data-testid={`st-copy-${x.n}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-stone-300 text-[10px] font-black uppercase tracking-wider text-navy hover:border-navy"><Copy className="h-3 w-3" /> Copy</button>
                                    <a href={`https://wa.me/?text=${encodeURIComponent(`GhumneChaley.com — ${prop.tour.name} (${prop.ref}). Instalment ${x.n} of ${inr(x.amount)} is due ${x.due_date}. Secure payment link: ${links[String(x.n)]}`)}`} target="_blank" rel="noreferrer" data-testid={`st-wa-${x.n}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-700 text-white text-[10px] font-black uppercase tracking-wider hover:bg-green-800"><MessageCircle className="h-3 w-3" /> WhatsApp</a>
                                    <button onClick={() => act(revokeSalesLink, x.n)} data-testid={`st-revoke-${x.n}`} className="px-3 py-1.5 rounded-full border border-red-200 text-red-600 text-[10px] font-black uppercase tracking-wider hover:bg-red-50">Revoke</button>
                                </span>
                            )}
                            {(!links[String(x.n)] && x.status !== "paid") && prop.status !== "draft" && (
                                <button onClick={() => act(regenerateSalesLink, x.n)} data-testid={`st-regen-${x.n}`} className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-stone-300 text-[10px] font-black uppercase tracking-wider text-navy hover:border-navy"><RefreshCw className="h-3 w-3" /> New link</button>
                            )}
                        </div>
                        {links[String(x.n)] && <p className="mt-1.5 text-[11px] text-stone-400 break-all">{links[String(x.n)]}</p>}
                    </div>
                ))}
            </div>
        </div>
    );
}

function PaymentHistory({ prop }) {
    return (
        <div data-testid="st-history">
            <h3 className="font-display font-black text-navy">Payment History</h3>
            <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead><tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-stone-200">
                        <th className="py-2 pr-3">Inst.</th><th className="py-2 pr-3">Amount</th><th className="py-2 pr-3">Due</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Paid At (UTC)</th><th className="py-2 pr-3">Razorpay Ref</th><th className="py-2 pr-3">Verified Via</th>
                    </tr></thead>
                    <tbody>
                        {(prop.installments || []).map((x) => (
                            <tr key={x.n} className="border-b border-stone-100" data-testid={`st-pay-row-${x.n}`}>
                                <td className="py-2.5 pr-3 font-bold">#{x.n}</td>
                                <td className="py-2.5 pr-3">{inr(x.amount)}</td>
                                <td className="py-2.5 pr-3">{x.due_date}</td>
                                <td className="py-2.5 pr-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${INST_CHIP[x.status] || INST_CHIP.pending}`}>{x.expired && x.status !== "paid" ? "expired" : x.status}</span></td>
                                <td className="py-2.5 pr-3 text-xs">{x.paid_at ? x.paid_at.replace("T", " ").slice(0, 19) : "—"}</td>
                                <td className="py-2.5 pr-3 text-xs break-all">{x.payment_id || x.order_id || "—"}</td>
                                <td className="py-2.5 pr-3 text-xs">{x.paid_source || "—"}</td>
                            </tr>
                        ))}
                        {!prop.installments?.length && <tr><td colSpan={7} className="py-4 text-sm text-stone-400">No schedule yet.</td></tr>}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function AuditTrail({ prop }) {
    return (
        <div data-testid="st-audit">
            <h3 className="font-display font-black text-navy">Audit Trail</h3>
            <ul className="mt-3 space-y-1.5">
                {[...(prop.audit || [])].reverse().map((a, i) => (
                    <li key={i} className="text-xs text-stone-600 flex gap-2 flex-wrap" data-testid={`st-audit-${i}`}>
                        <span className="text-stone-400 whitespace-nowrap">{(a.at || "").replace("T", " ").slice(0, 19)}</span>
                        <span className="font-bold text-navy">{a.action}</span>
                        <span>{a.detail}</span>
                        <span className="text-stone-400">— {a.actor?.name || a.actor}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function Detail({ id, token, isSuper, onBack, onEdit }) {
    const [prop, setProp] = useState(null);
    const [staff, setStaff] = useState([]);
    const [adjNote, setAdjNote] = useState("");
    const reload = useCallback(async () => setProp(await fetchSalesTour(token, id)), [token, id]);
    useEffect(() => { reload().catch(() => toast.error("Could not load proposal")); }, [reload]);
    useEffect(() => { if (isSuper) fetchStaff(token).then((r) => setStaff(r.users)).catch(() => {}); }, [isSuper, token]);
    if (!prop) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div>;

    const act = async (fn, msg) => {
        try { await fn(); toast.success(msg); await reload(); }
        catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Action failed"); }
    };
    const editable = !["fully_paid", "cancelled"].includes(prop.status);

    return (
        <div className="space-y-8" data-testid="st-detail">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <button onClick={onBack} data-testid="st-detail-back" className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-navy hover:text-saffron"><ArrowLeft className="h-4 w-4" /> All proposals</button>
                <div className="flex gap-2 flex-wrap">
                    {editable && <button onClick={() => onEdit(prop)} data-testid="st-edit-btn" className="px-4 py-2 rounded-full border border-stone-300 text-[11px] font-black uppercase tracking-wider text-navy hover:border-navy">Edit details</button>}
                    {prop.status !== "cancelled" && prop.status !== "fully_paid" && (
                        <button onClick={() => { if (window.confirm("Cancel this proposal? All payment links die immediately.")) act(() => cancelSalesTour(token, prop.id), "Proposal cancelled"); }} data-testid="st-cancel-btn" className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-red-200 text-red-600 text-[11px] font-black uppercase tracking-wider hover:bg-red-50"><Ban className="h-3 w-3" /> Cancel</button>
                    )}
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <h3 className="font-display font-black text-lg text-navy" data-testid="st-detail-ref">{prop.ref}</h3>
                            <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_CHIP[prop.status]}`} data-testid="st-detail-status">{statusLabel(prop.status)}</span>
                        </div>
                        <p className="text-sm font-bold text-navy mt-1">{prop.tour.name} — {prop.tour.destination}</p>
                        <p className="text-xs text-stone-500 mt-0.5">
                            {prop.tour.start_date || "?"} → {prop.tour.end_date || "?"} · {prop.tour.travellers} travellers · Total <b className="text-navy">{inr(prop.total_cost)}</b>
                        </p>
                        <p className="text-xs text-stone-500 mt-0.5">Customer: {prop.customer.name} · {prop.customer.phone}{prop.customer.email ? ` · ${prop.customer.email}` : ""}</p>
                        <p className="text-xs text-stone-500 mt-0.5">Sales owner: <b>{prop.owner?.name}</b> · Created {(prop.created_at || "").replace("T", " ").slice(0, 19)} UTC</p>
                        <div className="mt-3 grid grid-cols-3 gap-2">
                            <StatCard label="Collected" value={inr(prop.collected)} testid="st-collected" />
                            <StatCard label="Outstanding" value={inr(prop.outstanding)} testid="st-outstanding" />
                            <StatCard label="Next due" value={prop.next_due_date || "—"} testid="st-next-due" />
                        </div>
                    </div>
                    <div className="border-t border-stone-200 pt-5"><ScheduleBuilder prop={prop} token={token} onChanged={(p) => setProp((s) => ({ ...s, ...p }))} /></div>
                    <div className="border-t border-stone-200 pt-5"><ShareLinks prop={prop} token={token} onChanged={(p) => setProp(p)} /></div>
                    <div className="border-t border-stone-200 pt-5"><PaymentHistory prop={prop} /></div>
                </div>
                <div className="space-y-6">
                    {isSuper && (
                        <div className="rounded-xl border border-stone-200 p-4" data-testid="st-admin-tools">
                            <h4 className="text-[11px] font-bold uppercase tracking-wider text-stone-500">Super Admin Tools</h4>
                            <label className={`${lab} mt-3`}>Reassign owner</label>
                            <select className={inp} value="" onChange={(e) => e.target.value && act(() => reassignSalesTour(token, prop.id, e.target.value), "Ownership reassigned")} data-testid="st-reassign">
                                <option value="">Pick staff…</option>
                                {staff.filter((u) => u.active && u.id !== prop.owner?.id).map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                            </select>
                            <label className={`${lab} mt-3`}>Correction / refund note</label>
                            <textarea rows={2} className={inp} value={adjNote} onChange={(e) => setAdjNote(e.target.value)} placeholder="e.g. Refund of ₹5,000 issued in Razorpay on…" data-testid="st-adjustment-note" />
                            <button onClick={() => adjNote.trim() && act(() => addSalesAdjustment(token, prop.id, adjNote.trim()), "Adjustment recorded") && setAdjNote("")} data-testid="st-adjustment-btn" className="mt-2 inline-flex items-center gap-1 px-4 py-2 rounded-full bg-navy text-white text-[10px] font-black uppercase tracking-wider hover:bg-navy-light"><FileText className="h-3 w-3" /> Record</button>
                            <p className="mt-2 text-[10px] text-stone-400">Payments can never be edited or marked paid by hand — refunds happen in Razorpay, this keeps the record honest.</p>
                        </div>
                    )}
                    <AuditTrail prop={prop} />
                </div>
            </div>
        </div>
    );
}

export function SalesToursManager({ token, access }) {
    const [data, setData] = useState(null);
    const [filters, setFilters] = useState({ q: "", destination: "", status: "", owner_id: "", created_from: "", created_to: "", travel_from: "", travel_to: "", due_from: "", due_to: "", pay_status: "" });
    const [showFilters, setShowFilters] = useState(false);
    const [view, setView] = useState({ kind: "list" }); // list | form | detail
    const isSuper = !!access?.is_super;
    const canAll = isSuper || (access?.permissions || []).includes("sales_all");

    const load = useCallback(async (f = filters) => {
        try {
            const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
            setData(await fetchSalesTours(token, params));
        } catch { toast.error("Could not load sales tours"); }
    }, [token, filters]);
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const setF = (k, v) => setFilters((s) => ({ ...s, [k]: v }));
    const apply = () => load();
    const summary = data?.summary;

    if (view.kind === "form") {
        return (
            <section className="bg-white rounded-2xl border border-stone-300 p-6">
                <ProposalForm initial={view.initial || BLANK} token={token}
                    onCancel={() => setView({ kind: view.backTo || "list" })}
                    onSaved={(p) => { load(); setView({ kind: "detail", id: p.id }); }} />
            </section>
        );
    }
    if (view.kind === "detail") {
        return (
            <section className="bg-white rounded-2xl border border-stone-300 p-6">
                <Detail id={view.id} token={token} isSuper={isSuper}
                    onBack={() => { load(); setView({ kind: "list" }); }}
                    onEdit={(p) => setView({ kind: "form", backTo: "detail", initial: { id: p.id, tour: { ...BLANK.tour, ...p.tour }, customer: { ...p.customer, email: p.customer.email || "" }, total_cost: p.total_cost } })} />
            </section>
        );
    }

    return (
        <section className="bg-white rounded-2xl border border-stone-300 p-6" data-testid="sales-tours-manager">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="font-display font-black text-lg text-navy">Sales Tour Management</h2>
                    <p className="text-xs text-stone-500 mt-1">{canAll ? "All proposals across the sales team." : "Your tour proposals and instalment collections."}</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => exportSalesToursCsv(token, filters.owner_id || undefined)} data-testid="st-export-btn" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-stone-300 text-xs font-black uppercase tracking-wider text-navy hover:border-navy"><Download className="h-3.5 w-3.5" /> Export CSV</button>
                    <button onClick={() => setView({ kind: "form", initial: JSON.parse(JSON.stringify(BLANK)) })} data-testid="st-new-btn" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-saffron text-white text-xs font-display font-black uppercase tracking-wider hover:bg-saffron-dark"><Plus className="h-3.5 w-3.5" /> New Proposal</button>
                </div>
            </div>

            {summary && (
                <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <StatCard label="Proposals" value={summary.count} testid="st-sum-count" />
                    <StatCard label="Total tour value" value={inr(summary.total_value)} testid="st-sum-value" />
                    <StatCard label="Collected" value={inr(summary.collected)} testid="st-sum-collected" />
                    <StatCard label="Outstanding" value={inr(summary.outstanding)} testid="st-sum-outstanding" />
                </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2 items-end">
                <div className="w-56"><label className={lab}>Customer / mobile / ref</label><input className={inp} value={filters.q} onChange={(e) => setF("q", e.target.value)} data-testid="st-filter-q" /></div>
                <div className="w-44"><label className={lab}>Destination</label><input className={inp} value={filters.destination} onChange={(e) => setF("destination", e.target.value)} data-testid="st-filter-destination" /></div>
                <div><label className={lab}>Status</label>
                    <select className={inp} value={filters.status} onChange={(e) => setF("status", e.target.value)} data-testid="st-filter-status">
                        <option value="">All</option>
                        {["draft", "shared", "partially_paid", "fully_paid", "cancelled"].map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
                    </select>
                </div>
                {canAll && (
                    <div><label className={lab}>Sales person</label>
                        <select className={inp} value={filters.owner_id} onChange={(e) => setF("owner_id", e.target.value)} data-testid="st-filter-owner">
                            <option value="">Everyone</option>
                            {(data?.owners || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                        </select>
                    </div>
                )}
                <button onClick={() => setShowFilters((s) => !s)} data-testid="st-more-filters" className="px-4 py-2 rounded-full border border-stone-300 text-[11px] font-black uppercase tracking-wider text-navy hover:border-navy">{showFilters ? "Fewer filters" : "Date filters"}</button>
                <button onClick={apply} data-testid="st-apply-filters" className="px-5 py-2 rounded-full bg-navy text-white text-[11px] font-black uppercase tracking-wider hover:bg-navy-light">Apply</button>
            </div>
            {showFilters && (
                <div className="mt-2 flex flex-wrap gap-2 items-end" data-testid="st-date-filters">
                    {[["created_from", "Created from"], ["created_to", "Created to"], ["travel_from", "Travel from"], ["travel_to", "Travel to"], ["due_from", "Instalment due from"], ["due_to", "Instalment due to"]].map(([k, l]) => (
                        <div key={k}><label className={lab}>{l}</label><input type="date" className={inp} value={filters[k]} onChange={(e) => setF(k, e.target.value)} data-testid={`st-filter-${k}`} /></div>
                    ))}
                    <div><label className={lab}>Instalment payment status</label>
                        <select className={inp} value={filters.pay_status} onChange={(e) => setF("pay_status", e.target.value)} data-testid="st-filter-pay-status">
                            <option value="">Any</option>
                            {["pending", "paid", "failed", "expired", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                </div>
            )}

            {data === null ? (
                <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-saffron" /></div>
            ) : (
                <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead><tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-stone-200">
                            <th className="py-2 pr-3">Ref / Customer</th><th className="py-2 pr-3">Tour</th>{canAll && <th className="py-2 pr-3">Sales</th>}<th className="py-2 pr-3">Value</th><th className="py-2 pr-3">Collected</th><th className="py-2 pr-3">Outstanding</th><th className="py-2 pr-3">Instalments</th><th className="py-2 pr-3">Next Due</th><th className="py-2 pr-3">Status</th>
                        </tr></thead>
                        <tbody>
                            {data.proposals.map((p) => (
                                <tr key={p.id} className="border-b border-stone-100 hover:bg-cream/60 cursor-pointer" onClick={() => setView({ kind: "detail", id: p.id })} data-testid={`st-row-${p.ref}`}>
                                    <td className="py-3 pr-3"><p className="font-bold text-navy">{p.ref}</p><p className="text-xs text-stone-500">{p.customer.name} · {p.customer.phone}</p></td>
                                    <td className="py-3 pr-3"><p className="font-bold text-navy">{p.tour.destination}</p><p className="text-xs text-stone-500">{p.tour.start_date || "—"} · {p.tour.travellers} pax</p></td>
                                    {canAll && <td className="py-3 pr-3 text-xs font-bold text-navy">{p.owner?.name}</td>}
                                    <td className="py-3 pr-3 whitespace-nowrap font-bold">{inr(p.total_cost)}</td>
                                    <td className="py-3 pr-3 whitespace-nowrap text-green-700 font-bold">{inr(p.collected)}</td>
                                    <td className="py-3 pr-3 whitespace-nowrap">{inr(p.outstanding)}</td>
                                    <td className="py-3 pr-3 whitespace-nowrap text-xs">{p.paid_count} paid · {p.pending_count} due</td>
                                    <td className="py-3 pr-3 whitespace-nowrap text-xs">{p.next_due_date || "—"}</td>
                                    <td className="py-3 pr-3"><span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_CHIP[p.status]}`} data-testid={`st-status-${p.ref}`}>{statusLabel(p.status)}</span></td>
                                </tr>
                            ))}
                            {!data.proposals.length && <tr><td colSpan={9} className="py-8 text-center text-sm text-stone-400" data-testid="st-empty">No proposals yet — create your first tour proposal.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}
